import type { AgentRecord } from "@/services/studio/types";

export interface AgentOutcome {
  title: string;
  bullets: string[];
  artifacts: string[];
}

export function agentOutcome(agent: AgentRecord): AgentOutcome {
  const name = agent.name.toLowerCase();
  if (name.includes("code review")) {
    return {
      title: "Code review outcome",
      bullets: [
        "Finds risky Apex patterns, dependency impact, and likely regression points.",
        "Produces prioritized fixes with evidence from source and graph tools.",
        "Separates safe recommendations from changes that need approval.",
      ],
      artifacts: ["Review report", "Fix checklist", "Tool evidence JSON", "PDF summary"],
    };
  }
  if (name.includes("test")) {
    return {
      title: "Test outcome",
      bullets: [
        "Identifies classes or paths that need better test coverage.",
        "Drafts test strategy and candidate Apex test cases.",
        "Summarizes validation or test execution results when available.",
      ],
      artifacts: ["Coverage report", "Test plan", "Evidence JSON", "PDF summary"],
    };
  }
  if (name.includes("release") || name.includes("deployment")) {
    return {
      title: "Release outcome",
      bullets: [
        "Checks readiness, dependency risk, and validation priorities.",
        "Produces a release checklist with blockers and recommended order.",
        "Creates a report that can be shared with the team.",
      ],
      artifacts: ["Readiness report", "Release checklist", "Evidence JSON", "PDF summary"],
    };
  }
  if (name.includes("object") || name.includes("metadata") || name.includes("org")) {
    return {
      title: "Analysis outcome",
      bullets: [
        "Summarizes selected org or Metadata V1 scope.",
        "Finds relationships, dependencies, and risks using available tools.",
        "Produces a downloadable report with raw evidence kept separately.",
      ],
      artifacts: ["Analysis report", "HTML report", "Evidence JSON", "PDF summary"],
    };
  }
  if (agent.agent_type === "actionable") {
    return {
      title: "Change-ready outcome",
      bullets: [
        "Prepares a proposed change or fix with supporting evidence.",
        "Calls out what requires approval before any mutating action.",
        "Creates a report and implementation checklist.",
      ],
      artifacts: ["Action plan", "Approval checklist", "Evidence JSON", "PDF summary"],
    };
  }
  return {
    title: "Analysis outcome",
    bullets: [
      "Answers the request using scoped org metadata and tools.",
      "Highlights findings, risks, and next actions.",
      "Creates downloadable report artifacts.",
    ],
    artifacts: ["Query report", "HTML report", "Evidence JSON", "PDF summary"],
  };
}
